import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { track } from "@/lib/analytics";
import axios from "axios";
import { API } from "@/lib/api";
import { TOS_CURRENT_VERSION } from "@/pages/Legal";
import AssignFlowLogo from "@/components/AssignFlowLogo";
import { translateLegacyPath } from "@/lib/legacyRedirect";
import useAttributionCapture, { readStoredAttribution, clearStoredAttribution } from "@/hooks/useAttributionCapture";
import {
  Mail,
  Lock,
  User,
  ArrowRight,
  Loader2,
  Building2,
  Sparkles,
  Gift,
  PlayCircle,
} from "lucide-react";

export function LoginPage() {
  const { login, applySession, error, setError, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();
  const [search] = useSearchParams();
  const intentTier = (search.get("tier") || "").toLowerCase();
  const intentCycle = (search.get("cycle") || "monthly").toLowerCase();
  const hasIntent = ["pro", "hustler", "team"].includes(intentTier);

  const dest = hasIntent
    ? `/app/billing?tier=${intentTier}&cycle=${intentCycle}&autostart=1`
    : translateLegacyPath(loc.state?.from || "/u6");

  // Redirect an already-authenticated visitor. Done in an effect — calling
  // `nav()` inline during render throws a "Cannot update during render"
  // warning and starts an infinite loop in StrictMode.
  useEffect(() => {
    if (user && typeof user === "object") {
      nav(dest, { replace: true });
    }
  }, [user, dest, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const ok = await login(email, password);
    setLoading(false);
    if (ok) {
      track("login_completed", { tier: intentTier || null });
      nav(dest, { replace: true });
    }
  };

  const tryDemo = async () => {
    setDemoLoading(true);
    setError("");
    try {
      const storedRef = (localStorage.getItem("af_ref_code") || "").toUpperCase();
      const { data } = await axios.post(`${API}/auth/demo-login`, {
        ref: storedRef || null,
        source: "login_page",
      });
      if (data?.access_token) {
        applySession(data.user, data.access_token);
      }
      track("demo_login_completed", { seeded: !!data?.demo_seeded });
      nav("/u6", { replace: true });
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start the demo — please try again.");
    } finally {
      setDemoLoading(false);
    }
  };

  return <AuthShell title="Welcome back" sub={hasIntent ? `Sign in to continue to checkout — ${intentTier} (${intentCycle})` : "Sign in to your iSpeedToClose workspace"}>
    <form onSubmit={submit} className="flex flex-col gap-5" data-testid="login-form">
      <Field icon={Mail} label="Email" type="email" value={email} onChange={setEmail} testid="login-email-input" required />
      <Field icon={Lock} label="Password" type="password" value={password} onChange={setPassword} testid="login-password-input" required />
      {error && <div className="banner-err" data-testid="login-error">{error}</div>}
      <button type="submit" className="btn-money" disabled={loading} data-testid="login-submit-button">
        {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
        {loading ? "Signing in" : "Sign in"}
      </button>

      {/* Divider */}
      <div className="relative flex items-center gap-3 my-1">
        <div className="flex-1 h-px bg-slate-200" />
        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">or</span>
        <div className="flex-1 h-px bg-slate-200" />
      </div>

      {/* Try Demo — zero-friction entry for prospects */}
      <button
        type="button"
        onClick={tryDemo}
        disabled={demoLoading || loading}
        className="group relative flex items-center justify-center gap-2.5 w-full rounded-xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-white px-5 py-3.5 font-bold text-amber-900 shadow-sm hover:border-amber-400 hover:shadow-md hover:from-amber-100 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid="login-try-demo-button"
      >
        {demoLoading ? (
          <Loader2 size={18} className="animate-spin" />
        ) : (
          <PlayCircle size={18} className="text-amber-700 group-hover:scale-110 transition-transform" />
        )}
        <span>{demoLoading ? "Loading demo…" : "Try the demo — no signup"}</span>
        <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 uppercase tracking-wider">Live</span>
      </button>
      <div className="-mt-2 text-[11px] text-slate-500 text-center leading-relaxed">
        Instant sandbox with 6 pre-loaded properties, Jarvis AI, and full Command Center — no card, no email.
      </div>

      <div className="text-sm text-[var(--ink-2)]">
        New here? <Link to={hasIntent ? `/register?tier=${intentTier}&cycle=${intentCycle}` : "/register"} className="font-semibold text-[var(--ink)] underline" data-testid="link-to-register">Create an account</Link>
      </div>
    </form>
  </AuthShell>;
}

export function RegisterPage() {
  useAttributionCapture();
  const { register, error, setError, user } = useAuth();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [tosAgreed, setTosAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [referrer, setReferrer] = useState(null);
  const nav = useNavigate();
  const [search] = useSearchParams();
  // Preserve tier/cycle deep-link from landing page CTAs through registration.
  const intentTier = (search.get("tier") || "").toLowerCase();
  const intentCycle = (search.get("cycle") || "monthly").toLowerCase();
  // "Analyze my address" hero on the landing page passes the typed address here.
  // We use it to auto-create the user's first lead + kick off the report
  // immediately after signup so they land on a generated property analysis
  // before they've done any work.
  const seedAddress = (search.get("address") || "").trim();
  // VIP onboarding token — when present, redeem immediately after signup
  // to auto-promote the new user to a paid tier for N days. See
  // /api/admin/vip-links for how the operator generates these URLs.
  const vipToken = (search.get("vip") || "").trim();
  // Referral code — try query string first, then localStorage (set by /refer/:code).
  const refCode = (search.get("ref") || localStorage.getItem("af_ref_code") || "").toUpperCase();
  // UTM attribution — pulled from the URL params planted by marketing surfaces
  // (lead-magnet emails, social posts, paid ads). Forwarded to /api/auth/register.
  const utm = {
    utm_source:   search.get("utm_source")   || undefined,
    utm_campaign: search.get("utm_campaign") || undefined,
    utm_content:  search.get("utm_content")  || undefined,
    utm_medium:   search.get("utm_medium")   || undefined,
  };
  const tierLabel = ({ pro: "Pro", hustler: "Hustler", team: "Teams" })[intentTier];
  useEffect(() => { track("register_started", { tier: intentTier || null, cycle: intentCycle }); }, [intentTier, intentCycle]);
  useEffect(() => {
    if (!refCode) return;
    axios.get(`${API}/public/referrals/${refCode}`)
      .then((r) => setReferrer(r.data))
      .catch(() => setReferrer(null));
  }, [refCode]);

  const subtitle = tierLabel
    ? `Create your account to start ${tierLabel}${intentTier === "pro" ? " — 14-day free trial, no card today" : ""}.`
    : "Start managing wholesale deals in under a minute";

  const submit = async (e) => {
    e.preventDefault();
    if (!tosAgreed) {
      setError("Please agree to the Terms of Service, Privacy Policy, and Disclaimer to continue.");
      return;
    }
    setLoading(true);
    setError("");
    const extras = { ...utm, tos_agreed: true, tos_version: TOS_CURRENT_VERSION };
    if (referrer) extras.referral_code = refCode;
    // Deal-Won attribution — pass session token if we captured one from a
    // share-link visit. Backend credits the referrer on convert. Safe fallback:
    // no token means no attribution, register proceeds unchanged.
    const attribution = readStoredAttribution();
    if (attribution?.session_token) {
      extras.attribution_session = attribution.session_token;
    }
    const ok = await register(email, password, name, extras);
    setLoading(false);
    if (ok) {
      // Clear stored ref code + attribution once consumed.
      localStorage.removeItem("af_ref_code");
      clearStoredAttribution();
      track("register_completed", { tier: intentTier || null, cycle: intentCycle, has_ref: !!referrer });
      // VIP token redemption — auto-promotes the user to the granted tier.
      // Fire-and-forget; bad token shows a toast but doesn't block the flow.
      if (vipToken) {
        try {
          const r = await axios.post(`${API}/vip-links/redeem`,
            { token: vipToken },
            { headers: { Authorization: `Bearer ${localStorage.getItem("af_token")}` } },
          );
          track("vip_link_redeemed", { tier: r.data?.tier_granted });
          // Force a brief refresh so the AuthContext picks up the new tier.
        } catch (err) {
          track("vip_link_redeem_failed", { reason: err?.response?.data?.detail });
        }
      }
      // Magic onboarding flow: if a property address was passed in from the
      // landing-page hero, auto-create the lead (RentCast-enriched) and route
      // into the autogen analyzer so the user lands on a generated analysis.
      if (seedAddress) {
        try {
          const r = await axios.post(`${API}/onboarding/magic-lead`, {
            address: seedAddress,
            source: "landing_hero",
          }, { headers: { Authorization: `Bearer ${localStorage.getItem("af_token")}` } });
          track("register_magic_lead_created", { address: seedAddress });
          track("magic_register_completed", { address: seedAddress });
          nav(`/app/leads/${r.data.lead.id}/report?autogen=1`, { replace: true });
          return;
        } catch {
          // Magic lead seeding failure is non-fatal — fall through to /app.
        }
      }
      if (tierLabel) {
        nav(`/app/billing?tier=${intentTier}&cycle=${intentCycle}&autostart=1`, { replace: true });
      } else {
        nav("/u6", { replace: true });
      }
    }
  };

  return <AuthShell title="Create your workspace" sub={subtitle}>
    {/* 14-day Pro trial starburst — the single strongest conversion nudge on this page */}
    <div
      className="mb-3 flex items-center gap-3 rounded-xl px-4 py-3 bg-gradient-to-r from-amber-50 via-amber-50 to-emerald-50 border border-amber-200 shadow-sm"
      data-testid="register-trial-badge"
    >
      <div className="relative flex-shrink-0">
        <div className="w-11 h-11 rounded-full bg-amber-400 flex items-center justify-center shadow-inner">
          <Sparkles size={20} className="text-amber-900" />
        </div>
        <div className="absolute -inset-1 rounded-full bg-amber-300/40 blur animate-pulse pointer-events-none" />
      </div>
      <div className="flex-1 leading-tight">
        <div className="text-[15px] font-extrabold text-amber-900">
          Free 14-day Pro trial included
        </div>
        <div className="text-[12px] text-amber-800/90">
          Full AI Deal Intelligence · Jarvis unlimited · No credit card required
        </div>
      </div>
    </div>
    {seedAddress && (
      <div
        className="mb-1 flex items-start gap-2 rounded-lg px-3 py-2.5 text-sm border"
        style={{ background: "#ECFDF5", borderColor: "#A7F3D0", color: "#065F46" }}
        data-testid="register-seed-address-banner"
      >
        <Sparkles size={14} className="text-emerald-700 shrink-0 mt-0.5" />
        <span>
          We&apos;ll analyze <strong>{seedAddress}</strong> the moment you sign up —
          ARV, repair estimate, exit strategies, curb shot, and aerial map, ready in seconds.
        </span>
      </div>
    )}
    {referrer && (
      <div
        className="mb-1 flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm border border-amber-200"
        style={{ background: "#FEF3C7" }}
        data-testid="register-referral-banner"
      >
        <Gift size={14} className="text-amber-700 shrink-0" />
        <span className="text-amber-900">
          <strong>{referrer.referrer_name}</strong>
          {referrer.referrer_company ? ` (${referrer.referrer_company})` : ""}
          {" "}invited you — you both get <strong>1 free month</strong> when you upgrade.
        </span>
      </div>
    )}
    {tierLabel && (
      <div
        className="mb-1 flex items-center gap-2 rounded-lg px-3 py-2 text-sm"
        style={{ background: "#F59E0B22", color: "#7C4A0A" }}
        data-testid="register-intent-banner"
      >
        <Sparkles size={14} />
        <span>Selected plan: <strong>{tierLabel}</strong> · {intentCycle} billing</span>
      </div>
    )}
    <form onSubmit={submit} className="flex flex-col gap-5" data-testid="register-form">
      <Field icon={User} label="Full name" value={name} onChange={setName} testid="register-name-input" required minLength={1} />
      <Field icon={Mail} label="Email" type="email" value={email} onChange={setEmail} testid="register-email-input" required />
      <Field icon={Lock} label="Password" type="password" value={password} onChange={setPassword} testid="register-password-input" required minLength={6} />
      <label
        className="flex items-start gap-2.5 text-sm text-[var(--ink-2)] leading-relaxed cursor-pointer select-none"
        data-testid="register-tos-label"
      >
        <input
          type="checkbox"
          checked={tosAgreed}
          onChange={(e) => setTosAgreed(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-[var(--line)] accent-[var(--ink)] shrink-0"
          data-testid="register-tos-checkbox"
          required
          aria-required="true"
        />
        <span>
          I agree to the{" "}
          <Link to="/legal/terms" target="_blank" rel="noopener" className="underline font-semibold text-[var(--ink)]" data-testid="register-tos-terms-link">Terms of Service</Link>,{" "}
          <Link to="/legal/privacy" target="_blank" rel="noopener" className="underline font-semibold text-[var(--ink)]" data-testid="register-tos-privacy-link">Privacy Policy</Link>, and{" "}
          <Link to="/legal/disclaimer" target="_blank" rel="noopener" className="underline font-semibold text-[var(--ink)]" data-testid="register-tos-disclaimer-link">Disclaimer</Link>.
        </span>
      </label>
      {error && <div className="banner-err" data-testid="register-error">{error}</div>}
      <button type="submit" className="btn-money" disabled={loading || !tosAgreed} data-testid="register-submit-button">
        {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
        {loading ? "Creating account" : tierLabel ? `Continue to ${tierLabel} checkout` : "Create account"}
      </button>
      <div className="text-sm text-[var(--ink-2)]">
        Already have an account? <Link to={tierLabel ? `/login?tier=${intentTier}&cycle=${intentCycle}` : "/login"} className="font-semibold text-[var(--ink)] underline" data-testid="link-to-login">Sign in</Link>
      </div>
    </form>
  </AuthShell>;
}

function Field({ icon: Icon, label, type = "text", value, onChange, testid, ...rest }) {
  return (
    <label className="block">
      <span className="field-label">{label}</span>
      <div className="relative">
        <Icon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]" />
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="input pl-9"
          data-testid={testid}
          {...rest}
        />
      </div>
    </label>
  );
}

function AuthShell({ title, sub, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 exos">
      <div className="flex items-center justify-center p-8 relative">
        <div className="w-full max-w-sm relative z-10">
          <Link to="/" className="inline-flex items-center mb-10" data-testid="brand-link">
            <img src="/brand/wordmark-color.png" alt="iSpeedToClose" className="h-14 md:h-16 w-auto" width="243" height="64" />
          </Link>
          <span className="exos-eyebrow mb-5">Sign in</span>
          <h1 className="font-display font-black text-4xl tracking-tighter mb-3 text-slate-900 mt-4">{title}</h1>
          <p className="text-slate-600 text-sm mb-8 leading-relaxed">{sub}</p>
          {children}
          <div className="mt-10 pt-6 border-t border-slate-200 text-[11px] text-slate-500" data-testid="auth-copyright">
            © {new Date().getFullYear()} Perrin Properties, LLC
          </div>
        </div>
      </div>
      <div className="hidden lg:block relative overflow-hidden bg-[#F1EEE6]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/brand/login-hero.jpg')" }}
        />
        {/* Soft warm wash — brightens the image without blowing it out */}
        <div className="absolute inset-0 bg-gradient-to-br from-amber-50/10 via-transparent to-transparent" />
        {/* Bottom-left darkening ONLY where the text lives — keeps copy readable */}
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-950/75 via-slate-900/25 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-end p-14 max-w-xl">
          <div className="rounded-2xl bg-slate-950/55 backdrop-blur-sm px-7 py-8 ring-1 ring-white/10 shadow-2xl">
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/25 border border-emerald-400/50 text-[11px] font-bold uppercase tracking-[0.14em] mb-5 w-fit"
              style={{ color: "#A5B4FC" }}
            >
              Speed is the deal
            </span>
            <h2
              className="font-display font-black text-5xl tracking-tighter leading-[0.98] mb-5"
              style={{ color: "#FFFFFF" }}
            >
              Close more deals.<br />
              <span style={{ color: "#A5B4FC" }}>With less chaos.</span>
            </h2>
            <p
              className="leading-relaxed text-lg"
              style={{ color: "#F1F5F9" }}
            >
              Property CRM · Immutable Timeline · Deal Intelligence Trilogy · Jarvis AI — the operating system for real estate investors.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
